import React, { useRef, useState } from 'react';
import { Close, Print, Receipt as ReceiptIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';

const ReceiptModal = ({ invoice, isOpen, onClose }) => {
    const receiptRef = useRef(null);
    const [showReceipt, setShowReceipt] = useState(false);
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

    // Currency configuration
    const currencyOptions = {
        NGN: { 
            symbol: '₦', 
            name: 'Nigerian Naira', 
            code: 'NGN',
            account: {
                name: 'Health System Alliance Partners Ltd',
                bank: 'Access Bank Plc',
                number: '1951389808'
            }
        },
        USD: { 
            symbol: '$', 
            name: 'US Dollar', 
            code: 'USD',
            account: {
                name: 'CONNECT HEALTH PRO',
                bank: 'AfrAsia Bank Ltd',
                number: '143493000000021',
                iban: 'MU73AFBL2501143493000000021EUR',
                swift: 'AFBLMUMU',
                address: 'Bowen Square, 10 Dr Ferriere Street, Port Louis, Mauritius'
            }
        }
    };

    // Get currency from invoice (either from currency field or extract from subject)
    const getCurrency = (invoice) => {
        if (invoice?.currency && currencyOptions[invoice?.currency]) {
            return invoice?.currency;
        }
        
        // Fallback: try to extract from subject
        const subject = invoice?.invoiceSubject || '';
        if (subject.includes('$') || subject.toLowerCase().includes('usd')) {
            return 'USD';
        }
        if (subject.includes('₦') || subject.toLowerCase().includes('ngn')) {
            return 'NGN';
        }
        
        return 'NGN'; // Default
    };

    // Remove currency symbol from subject for display
    const removeCurrencyFromSubject = (subject) => {
        if (!subject) return '';
        return subject.replace(/[₦$]\s*$/, '').trim();
    };

    const currentCurrency = getCurrency(invoice);
    const currencyConfig = currencyOptions[currentCurrency];

    if (!isOpen || !invoice) return null;

    const handlePrintInvoice = () => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow.document;
        const receiptContent = receiptRef.current.innerHTML;
        const documentTitle = showReceipt ? `Receipt #${invoice?.id}` : `Invoice #${invoice?.id}`;

        iframeDoc.open();
        iframeDoc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
                        line-height: 1.5;
                        color: #1f2937;
                        background: white;
                        padding: 2rem;
                        font-size: 14px;
                    }
                    
                    /* Layout Classes */
                    .p-10 { padding: 2rem; }
                    .mb-8 { margin-bottom: 2rem; }
                    .mb-6 { margin-bottom: 1.5rem; }
                    .mb-4 { margin-bottom: 1rem; }
                    .mb-3 { margin-bottom: 0.75rem; }
                    .mb-2 { margin-bottom: 0.5rem; }
                    .mb-1 { margin-bottom: 0.25rem; }
                    .pt-4 { padding-top: 1rem; }
                    .p-4 { padding: 1rem; }
                    .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
                    .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
                    .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
                    
                    /* Flex & Grid */
                    .flex { display: flex; }
                    .justify-between { justify-content: space-between; }
                    .items-start { align-items: flex-start; }
                    .items-center { align-items: center; }
                    .grid { display: grid; }
                    .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                    .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                    .gap-8 { gap: 2rem; }
                    .gap-4 { gap: 1rem; }
                    .space-y-0\\.5 > * + * { margin-top: 0.125rem; }
                    
                    /* Typography */
                    .text-2xl { font-size: 1.5rem; line-height: 2rem; }
                    .text-lg { font-size: 1.125rem; line-height: 1.75rem; }
                    .text-sm { font-size: 0.875rem; line-height: 1.25rem; }
                    .text-xs { font-size: 0.75rem; line-height: 1rem; }
                    .text-\\[11px\\] { font-size: 11px; line-height: 1.2; }
                    .text-\\[10px\\] { font-size: 10px; line-height: 1.2; }
                    .font-bold { font-weight: 700; }
                    .font-semibold { font-weight: 600; }
                    .font-medium { font-weight: 500; }
                    .uppercase { text-transform: uppercase; }
                    .tracking-wide { letter-spacing: 0.025em; }
                    .text-right { text-align: right; }
                    .text-left { text-align: left; }
                    
                    /* Colors */
                    .text-gray-900 { color: #111827; }
                    .text-gray-800 { color: #1f2937; }
                    .text-gray-700 { color: #374151; }
                    .text-gray-600 { color: #4b5563; }
                    .text-gray-500 { color: #6b7280; }
                    .text-gray-400 { color: #9ca3af; }
                    .text-green-600 { color: #16a34a; }
                    .text-green-700 { color: #15803d; }
                    .bg-gray-50 { background-color: #f9fafb; }
                    .bg-gray-100 { background-color: #f3f4f6; }
                    .bg-green-100 { background-color: #dcfce7; }
                    
                    /* Borders & Shapes */
                    .rounded-lg { border-radius: 0.5rem; }
                    .border { border: 1px solid #e5e7eb; }
                    .border-t { border-top: 1px solid #e5e7eb; }
                    .border-t-2 { border-top: 2px solid #e5e7eb; }
                    .border-b { border-bottom: 1px solid #e5e7eb; }
                    .border-gray-200 { border-color: #e5e7eb; }
                    .border-gray-100 { border-color: #f3f4f6; }
                    .border-green-500 { border-color: #22c55e; }
                    .border-2 { border-width: 2px; }
                    
                    /* Text utilities */
                    .break-all { word-break: break-all; }
                    .leading-tight { line-height: 1.25; }
                    
                    /* Sizing */
                    .min-w-\\[140px\\] { min-width: 140px; }
                    .w-full { width: 100%; }
                    
                    /* Table Specific */
                    table { 
                        width: 100%; 
                        border-collapse: collapse;
                        margin: 0;
                    }
                    th { 
                        text-align: left; 
                        padding: 0.5rem 0; 
                        font-weight: 600;
                        border-bottom: 1px solid #e5e7eb;
                    }
                    td { 
                        padding: 0.75rem 0; 
                        border-bottom: 1px solid #f3f4f6;
                        vertical-align: top;
                    }
                    .last\\:border-b-0:last-child { border-bottom: none; }
                    tbody tr { border-bottom: 1px solid #f3f4f6; }
                    tbody tr:last-child { border-bottom: none; }
                    thead { background-color: #f9fafb; }
                    
                    /* Overflow handling */
                    .overflow-hidden { overflow: hidden; }
                    .inline-flex { display: inline-flex; }
                    .flex-col { flex-direction: column; }
                    .items-end { align-items: flex-end; }
                    
                    /* Print specific styles */
                    @media print {
                        @page { 
                            margin: 1cm; 
                            size: A4; 
                        }
                        body { 
                            -webkit-print-color-adjust: exact; 
                            print-color-adjust: exact;
                            padding: 0;
                            font-size: 12px;
                        }
                        .bg-gray-50,
                        .bg-gray-100,
                        .bg-green-100 {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        /* Ensure colors print correctly */
                        * {
                            -webkit-print-color-adjust: exact !important;
                            color-adjust: exact !important;
                        }
                    }
                </style>
            </head>
            <body>${receiptContent}</body>
            </html>
        `);
        iframeDoc.close();

        iframe.onload = () => {
            setTimeout(() => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 100);
            }, 250);
        };
    };

    const handleGenerateReceipt = () => {
        setShowReceipt(true);
        toast.success('Payment confirmation generated!');
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-all duration-300 overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[650px] transform transition-all duration-300 my-8 max-h-[95vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="bg-green-600 p-6 rounded-t-3xl sticky top-0 z-50 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-2xl font-bold text-white">
                                {showReceipt ? 'Payment Confirmation' : 'Invoice'}
                            </h3>
                            
                        </div>
                        <div className="flex items-center gap-2">
                            {showReceipt && (
                                <div className="flex items-center gap-2 bg-white/20 rounded-xl px-3 py-2">
                                    <label className="text-white text-xs font-semibold">Payment Date:</label>
                                    <input
                                        type="date"
                                        value={paymentDate}
                                        onChange={(e) => setPaymentDate(e.target.value)}
                                        className="bg-white/90 text-gray-800 text-xs rounded px-2 py-1 border-0 focus:outline-none focus:ring-1 focus:ring-white"
                                    />
                                </div>
                            )}
                            {!showReceipt && (
                                <button
                                    onClick={handleGenerateReceipt}
                                    className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2"
                                >
                                    <ReceiptIcon className="w-4 h-4" />
                                    Generate Invoice
                                </button>
                            )}
                            <button
                                onClick={handlePrintInvoice}
                                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2"
                            >
                                <Print className="w-4 h-4" />
                                Print
                            </button>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-300"
                            >
                                <Close className="text-white" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Receipt Content */}
                <div className="flex-1 overflow-y-auto bg-white">
                    <div ref={receiptRef} className="p-10 bg-white">
                        {/* Header Section */}
                        <div className="flex justify-between items-start mb-8">
                            {/* Left - Company Info */}
                            <div>
                                <h1 className="text-2xl font-bold mb-1" style={{ color: '#22C55E' }}>
                                    ConnectHealth<span style={{ color: '#16A34A' }}>Pro</span>
                                </h1>
                                <p className="text-xs text-gray-500 mb-4">
                                    Info@connecthealthpro.com | +2348062396839
                                </p>
                                <div className="text-xs space-y-0.5">
                                    <p className="font-semibold text-gray-800 uppercase tracking-wide text-[10px]">
                                        BILLED TO
                                    </p>
                                    <p className="font-bold text-gray-900">{invoice?.companyName}</p>
                                    <p className="text-gray-600">{invoice?.companyAddress}</p>
                                    <p className="text-gray-600">{invoice?.emailAddress || 'N/A'}</p>
                                    <p className="text-gray-600">{invoice?.phoneNumber}</p>
                                </div>
                            </div>

                            {/* Right - Receipt Badge */}
                            <div className="text-right">
                                <div className="inline-flex flex-col items-end">
                                    <div className="bg-gray-100 px-4 py-2 rounded-lg mb-3 text-center min-w-[140px]">
                                        <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                                            {showReceipt ? 'Confirmation' : 'Invoice'}
                                        </p>
                                        <p className="text-xs text-gray-400 mb-1">
                                            CHP{invoice?.id.toString().padStart(8, '0')}-01
                                        </p>
                                        <div className="border-t border-gray-300 pt-2 mt-2">
                                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                                                {showReceipt ? 'AMOUNT PAID' : 'TOTAL AMOUNT'}
                                            </p>
                                            <p className="text-lg font-bold" style={{ color: showReceipt ? '#16A34A' : '#22C55E' }}>
                                                {currencyConfig.symbol}{invoice?.invoiceTotal?.toLocaleString('en-US', { 
                                                    minimumFractionDigits: 2, 
                                                    maximumFractionDigits: 2 
                                                }) || '0.00'}
                                            </p>
                                        </div>
                                    </div>
                                    {showReceipt && (
                                        <div className="bg-green-100 border-2 border-green-500 px-4 py-2 rounded-lg">
                                            <p className="text-xs font-bold text-green-700 uppercase">✓ PAYMENT CONFIRMED</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Dates Section */}
                        <div className="grid grid-cols-2 gap-8 mb-6 text-xs">
                            <div>
                                <p className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">
                                    {showReceipt ? 'PAYMENT CONFIRMATION DATE' : 'INVOICE DATE'}
                                </p>
                                <p className="font-semibold text-gray-900">
                                    {showReceipt 
                                        ? new Date(paymentDate).toLocaleDateString('en-GB', { 
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric'
                                        }).replace(/\//g, '.')
                                        : new Date(invoice?.createdAt).toLocaleDateString('en-GB', { 
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric'
                                        }).replace(/\//g, '.')
                                    }
                                </p>
                            </div>
                            {!showReceipt && (
                                <div>
                                    <p className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">DUE DATE</p>
                                    <p className="font-semibold text-gray-900">
                                        {new Date(
                                            new Date(invoice?.createdAt).setDate(
                                                new Date(invoice?.createdAt).getDate() + 15
                                            )
                                        ).toLocaleDateString('en-GB', { 
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric'
                                        }).replace(/\//g, '.')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Service/Product Section */}
                        <div className="mb-6">
                            <p className="text-gray-900 font-bold text-xs mb-3">
                                {removeCurrencyFromSubject(invoice?.invoiceSubject) || 'Payment Details'}
                            </p>
                            
                            {showReceipt ? (
                                /* Receipt format - showing items without individual prices */
                                <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                                    <table className="w-full text-xs">
                                        <thead className="bg-gray-50">
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 text-gray-500 font-semibold uppercase tracking-wide text-[10px]">#</th>
                                                <th className="text-left py-2 px-3 text-gray-500 font-semibold uppercase tracking-wide text-[10px]">
                                                    ITEM / DESCRIPTION
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white">
                                            {invoice?.invoiceItems?.map((item, index) => (
                                                <tr key={item.id || index} className="border-b border-gray-100 last:border-b-0">
                                                    <td className="py-3 px-3 text-gray-700">{index + 1}</td>
                                                    <td className="py-3 px-3">
                                                        <p className="font-medium text-gray-900">{item.description}</p>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                /* Invoice format - showing items with prices */
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="text-left py-2 text-gray-500 font-semibold uppercase tracking-wide text-[10px]">#</th>
                                            <th className="text-left py-2 text-gray-500 font-semibold uppercase tracking-wide text-[10px]">
                                                ITEM / DESCRIPTION
                                            </th>
                                            <th className="text-right py-2 text-gray-500 font-semibold uppercase tracking-wide text-[10px]">
                                                SUBTOTAL
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice?.invoiceItems?.map((item, index) => (
                                            <tr key={item.id || index} className="border-b border-gray-100">
                                                <td className="py-3 text-gray-700">{index + 1}</td>
                                                <td className="py-3">
                                                    <p className="font-medium text-gray-900">{item.description}</p>
                                                </td>
                                                <td className="py-3 text-right font-medium text-gray-900">
                                                    {currencyConfig.symbol}{parseFloat(item.amount).toLocaleString('en-US', { 
                                                        minimumFractionDigits: 2, 
                                                        maximumFractionDigits: 2 
                                                    })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Total Section */}
                        <div className="border-t-2 border-gray-200 pt-4 mb-8">
                            <div className="flex justify-between items-center text-sm font-bold">
                                <span className="text-gray-900">Total Amount {showReceipt ? 'Paid' : 'Due'}</span>
                                <span className="text-gray-900">
                                    {currencyConfig.symbol}{invoice?.invoiceTotal?.toLocaleString('en-US', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                    }) || '0.00'}
                                </span>
                            </div>
                            {!showReceipt && (
                                <p className="text-[10px] text-gray-500 mt-1">
                                    Payment terms: Due within 15 days of invoice date.
                                </p>
                            )}
                            {showReceipt && (
                                <p className="text-[10px] text-green-600 mt-2 font-semibold">
                                    ✓ Payment has been successfully processed and confirmed.
                                </p>
                            )}
                        </div>

                        {/* Account Details Section */}
                        <div className="bg-gray-50 p-4 rounded-lg mb-6">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-3 font-semibold">
                                {showReceipt ? 'PAYMENT RECEIVED TO' : 'PAYMENT INSTRUCTIONS'} ({currencyConfig.name})
                            </p>
                            
                            {currentCurrency === 'NGN' ? (
                                /* Nigerian Naira Account */
                                <div className="grid grid-cols-3 gap-4 text-xs">
                                    <div>
                                        <p className="text-gray-500 text-[10px] mb-1">Account Name</p>
                                        <p className="font-semibold text-gray-900">{currencyConfig.account.name}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-[10px] mb-1">Bank Name</p>
                                        <p className="font-semibold text-gray-900">{currencyConfig.account.bank}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-[10px] mb-1">Account Number</p>
                                        <p className="font-semibold text-gray-900">{currencyConfig.account.number}</p>
                                    </div>
                                </div>
                            ) : (
                                /* USD International Account */
                                <div>
                                    <div className="grid grid-cols-2 gap-4 text-xs mb-3">
                                        <div>
                                            <p className="text-gray-500 text-[10px] mb-1">Account Name</p>
                                            <p className="font-semibold text-gray-900">{currencyConfig.account.name}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 text-[10px] mb-1">Account Number</p>
                                            <p className="font-semibold text-gray-900">{currencyConfig.account.number}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 text-[10px] mb-1">Bank Name</p>
                                            <p className="font-semibold text-gray-900">{currencyConfig.account.bank}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 text-[10px] mb-1">Swift Code</p>
                                            <p className="font-semibold text-gray-900">{currencyConfig.account.swift}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-xs">
                                        <div>
                                            <p className="text-gray-500 text-[10px] mb-1">IBAN</p>
                                            <p className="font-semibold text-gray-900 break-all">{currencyConfig.account.iban}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 text-[10px] mb-1">Bank Address</p>
                                            <p className="font-semibold text-gray-900 text-[10px] leading-tight">
                                                {currencyConfig.account.address}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="mb-6">
                            <p className="text-xs font-semibold text-gray-900 mb-1">
                                {showReceipt 
                                    ? 'Thank you for your payment.' 
                                    : 'Thank you for your business.'}
                            </p>
                            <p className="text-[10px] text-gray-500">
                                For any inquiries, please contact us at Info@connecthealthpro.com
                            </p>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="p-6 bg-gray-50 rounded-b-3xl flex justify-end gap-3 flex-shrink-0">
                    {showReceipt && (
                        <button
                            onClick={() => setShowReceipt(false)}
                            className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300"
                        >
                            Back to Invoice
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReceiptModal;
